import type { PendingSourceFragment } from '@mnd-apps/publish/generated/types';
import IconPen from '@mnd-frontend/icons/components/Pen';
import IconThumbnail from '@mnd-frontend/icons/components/Thumbnail';
import { media } from '@mnd-frontend/ui/publish/components/utils';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import {
  GREY_200,
  GREY_300,
  GREY_700,
  GREY_900,
  GREY_1000,
  RADIUS_LG,
  RADIUS_MD,
  SIZING_2,
  SIZING_2_5,
  SIZING_3,
  SIZING_6,
  SIZING_8,
  SPACING_0_5,
  SPACING_2,
  SPACING_4,
} from '@mnd-frontend/ui/shared/tokens';
import {
  type Dispatch,
  lazy,
  type MutableRefObject,
  type SetStateAction,
  Suspense,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PendingSourceDocument, usePendingSourceUpdateMutation } from '../../../generated/hooks';
import { useOnboardingLocaleText } from '../hooks/useOnboardingLocaleText';
import { STEP } from '../types';

const OnboardingModalBrand = lazy(() => import('../components/OnboardingModal'));

const LogoFrame = styled.div`
  width: ${SIZING_8};
  height: ${SIZING_8};
  background-color: white;
  border-radius: ${RADIUS_MD};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BrandLogoContainer = styled.div`
  width: 5.5rem;
  min-height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;

  .logo-img {
    object-fit: contain;
    height: auto;
    width: 100%;
    max-width: ${SIZING_6};
  }
`;

const BrandSearchNameWrapper = styled.div`
  flex: 1;
  justify-items: self-start;
`;

const BrandName = styled.div`
  font-size: ${SIZING_2_5};
  font-weight: 600;
  color: ${GREY_1000};
  line-height: normal;
  text-align: left;
  margin-bottom: ${SPACING_0_5};

  ${media.greaterThan('phablet')} {
    font-size: ${SIZING_3};
  }
`;

const BrandUrl = styled.div`
  font-size: ${SIZING_2};
  font-weight: 400;
  color: ${GREY_1000};
  line-height: normal;
  text-align: left;
  overflow-wrap: anywhere;

  ${media.greaterThan('phablet')} {
    overflow-wrap: normal;
  }
`;

const BrandSearchProfileContainer = styled.button`
  width: 100%;
  display: flex;
  flex-direction: row;
  padding: ${SPACING_2} ${SPACING_4};
  align-items: center;
  gap: ${SPACING_2};
  border-radius: ${RADIUS_LG};
  background-color: ${GREY_200};
  height: 7rem;
  border: none;
  transition: background-color 0.25s ease;

  &:hover {
    background-color: ${GREY_300};
  }

  &:focus-visible {
    background-color: ${GREY_300};
    outline: none;
  }

  ${media.lessThan('tablet')} {
    padding: ${SPACING_2};
  }
`;

const EditIconWrapper = styled.span`
  display: inline-flex;
  color: ${GREY_700}; 
  transition: color 0.25s ease;

  ${BrandSearchProfileContainer}:hover &,
  ${BrandSearchProfileContainer}:focus-visible & {
    color: ${GREY_900};
  }
`;

type StepBrandProps = {
  originalPendingSource: PendingSourceFragment;
  setSelectedBrandIds: Dispatch<SetStateAction<string[]>>;
  trackBrandNameChange: MutableRefObject<boolean>;
  trackBrandUrlChange: MutableRefObject<boolean>;
};

const StepBrand = ({
  originalPendingSource,
  setSelectedBrandIds,
  trackBrandNameChange,
  trackBrandUrlChange,
}: StepBrandProps) => {
  const { t } = useTranslation();
  const { showFailureToast } = useToasts();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const localeText = useOnboardingLocaleText()[STEP.BRAND];

  const [brandName, setBrandName] = useState(originalPendingSource.brandName ?? '');
  const [brandUrl, setBrandUrl] = useState(originalPendingSource.brandUrl ?? '');
  const initialRef = useRef({
    currentBrandName: originalPendingSource.brandName,
    currentBrandUrl: originalPendingSource.brandUrl,
  });

  const handleOpenEditModalOnClick = () => setIsEditModalOpen(true);

  // User edits her own brand
  const [updatePendingSource, { loading, data, reset }] = usePendingSourceUpdateMutation({
    refetchQueries: [PendingSourceDocument, 'PendingSource'],
    onCompleted: newData => {
      const error = newData.pendingSourceUpdate?.error;
      if (error) {
        if (error.__typename !== 'PendingSourceUpdateValidationError') {
          showFailureToast(error.message);
        }
      } else {
        trackBrandNameChange.current = brandName !== initialRef.current.currentBrandName;
        trackBrandUrlChange.current = brandUrl !== initialRef.current.currentBrandUrl;
        setSelectedBrandIds([]);
        setIsEditModalOpen(false);
      }
    },
    onError: () => showFailureToast(t('onboarding_trial_error_update')),
  });

  return (
    <>
      <BrandSearchProfileContainer onClick={handleOpenEditModalOnClick}>
        <LogoFrame>
          {originalPendingSource?.logoUrl ? (
            <BrandLogoContainer>
              <img
                className="logo-img"
                src={originalPendingSource.logoUrl}
                alt=""
                width="48"
                height="48"
              />
            </BrandLogoContainer>
          ) : (
            <IconThumbnail $size="md" />
          )}
        </LogoFrame>
        <BrandSearchNameWrapper>
          <BrandName>{originalPendingSource.brandName}</BrandName>
          <BrandUrl>{originalPendingSource.brandUrl}</BrandUrl>
        </BrandSearchNameWrapper>
        <EditIconWrapper aria-hidden>
          <IconPen />
        </EditIconWrapper>
      </BrandSearchProfileContainer>
      <Suspense>
        <OnboardingModalBrand
          type={STEP.BRAND}
          currentStepLocaleText={localeText}
          pendingSourceId={originalPendingSource.id}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onUpdateMutation={updatePendingSource}
          onUpdateReset={reset}
          updating={loading}
          updatedSource={data?.pendingSourceUpdate}
          fieldController={{ brandName, setBrandName, brandUrl, setBrandUrl }}
        />
      </Suspense>
    </>
  );
};

export default StepBrand;
